import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, FileText, ChevronRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";

const STATUSES = [
  { v: "neu", l: "Neu" },
  { v: "in_bearbeitung", l: "In Bearbeitung" },
  { v: "angebot_versendet", l: "Angebot versendet" },
  { v: "angenommen", l: "Angenommen" },
  { v: "abgelehnt", l: "Abgelehnt" },
];

function StatusBadge({ status }) {
  const map = {
    neu: "bg-[#E8ECE1] text-[#2C3E2D]",
    in_bearbeitung: "bg-[#FDF3D8] text-[#8A6100]",
    angebot_versendet: "bg-[#DBE7F1] text-[#1F3B54]",
    angenommen: "bg-[#DCEBD4] text-[#3B5C2C]",
    abgelehnt: "bg-[#F5D9CC] text-[#7A2E14]",
  };
  const label = STATUSES.find((s) => s.v === status)?.l || status;
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${map[status] || "bg-slate-100"}`}>{label}</span>;
}

export default function Requests() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/requests");
      setItems(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/requests/${id}/status`, { status });
      toast.success("Status aktualisiert");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createQuoteFrom = (req) => {
    localStorage.setItem("blessing_prefill_quote", JSON.stringify({
      customer_id: req.customer_id,
      request_id: req.id,
      customer_name: req.customer_name,
      leistungen: req.leistungen,
    }));
    nav("/crm/angebote?new=1");
  };

  return (
    <div data-testid="crm-requests">
      <div className="mb-5 md:mb-8 flex items-start justify-between gap-3 md:gap-4 flex-wrap">
        <div>
          <div className="hidden md:block text-xs font-medium text-[#6B7F3E] uppercase tracking-wider mb-2">CRM</div>
          <h1 className="hidden md:block font-display text-3xl font-semibold text-[#1A201A] tracking-tight">Anfragen</h1>
          <p className="text-sm text-[#5C6B5D] mt-1">Alle eingehenden Kundenanfragen von der Landingpage.</p>
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        {loading && <div className="p-8 text-sm text-[#5C6B5D]">Lädt…</div>}
        {!loading && items.length === 0 && (
          <div className="p-16 text-center text-sm text-[#5C6B5D]">
            Noch keine Anfragen. Sobald sich Kunden über die Landingpage melden, erscheinen sie hier.
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="crm-table w-full text-sm">
              <thead className="bg-[#F5F6F0] text-[11px] uppercase tracking-wider text-[#5C6B5D]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Kunde</th>
                  <th className="text-left px-4 py-3 font-medium">Typ</th>
                  <th className="text-left px-4 py-3 font-medium">Leistungen</th>
                  <th className="text-left px-4 py-3 font-medium">Dringlichkeit</th>
                  <th className="text-left px-4 py-3 font-medium">Status</th>
                  <th className="text-right px-4 py-3 font-medium">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} data-testid={`request-row-${r.id}`} className="border-t border-[#E5E7EB] hover:bg-[#FAFAF5]">
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium text-[#1A201A]">{r.customer_name}</div>
                      <div className="text-xs text-[#5C6B5D]">{r.customer_email}</div>
                      {r.warnings?.length > 0 && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-[#B85C38]">
                          <AlertTriangle className="w-3 h-3" /> {r.warnings.length} Hinweis(e)
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top" data-label="Typ">
                      <Badge variant="secondary" className="capitalize">{r.objekt_art}</Badge>
                    </td>
                    <td className="px-4 py-3 align-top max-w-[280px] crm-td-stack" data-label="Leistungen">
                      <div className="text-xs text-[#4A5D4E] line-clamp-2">
                        {r.leistungen.join(", ") || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top" data-label="Dringlichkeit">
                      <span className={`text-xs font-medium capitalize ${
                        r.dringlichkeit === "hoch" ? "text-[#B85C38]" :
                        r.dringlichkeit === "mittel" ? "text-[#8A6100]" : "text-[#5C6B5D]"
                      }`}>
                        {r.dringlichkeit}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top" data-label="Status">
                      <Select value={r.status} onValueChange={(v) => changeStatus(r.id, v)}>
                        <SelectTrigger className="h-8 text-xs w-[170px]" data-testid={`request-status-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 align-top text-right crm-td-actions">
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setSelected(r)}
                          data-testid={`request-view-${r.id}`}
                        >
                          Details
                        </Button>
                        <Button
                          size="sm"
                          className="bg-[#6B7F3E] hover:bg-[#5A6B34] text-white"
                          onClick={() => createQuoteFrom(r)}
                          data-testid={`request-quote-${r.id}`}
                        >
                          <FileText className="w-3.5 h-3.5 mr-1" /> Angebot
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="bg-white overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle className="font-display text-2xl">
              Anfrage von {selected?.customer_name}
            </SheetTitle>
          </SheetHeader>
          {selected && (
            <div className="mt-6 space-y-4 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#5C6B5D] mb-1">Kontakt</div>
                <div>{selected.customer_name}</div>
                <div className="text-[#4A5D4E]">{selected.customer_email}</div>
                <div className="text-[#4A5D4E]">{selected.customer_phone}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#5C6B5D] mb-1">Objekt</div>
                <div>Typ: {selected.objekt_art}</div>
                {selected.grundstuecksgroesse && <div>Grundstück: {selected.grundstuecksgroesse} m²</div>}
                {selected.objektgroesse && <div>Fläche: {selected.objektgroesse} m²</div>}
                {selected.turnus && <div>Turnus: {selected.turnus}</div>}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-[#5C6B5D] mb-1">Leistungen</div>
                <ul className="list-disc list-inside">
                  {selected.leistungen.map((l) => <li key={l}>{l}</li>)}
                </ul>
              </div>
              {selected.wunschtermin && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#5C6B5D] mb-1">Wunschtermin</div>
                  <div>{new Date(selected.wunschtermin).toLocaleString("de-DE")}</div>
                </div>
              )}
              {selected.notes && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#5C6B5D] mb-1">Notizen</div>
                  <div className="text-[#4A5D4E]">{selected.notes}</div>
                </div>
              )}
              {selected.warnings?.length > 0 && (
                <div className="p-3 bg-[#FDF3D8] border border-[#F5C874] rounded-lg">
                  <div className="text-xs font-medium text-[#8A6100] mb-1">Qualitätshinweise</div>
                  <ul className="text-xs text-[#8A6100] list-disc list-inside">
                    {selected.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              <Button
                className="w-full bg-[#6B7F3E] hover:bg-[#5A6B34]"
                onClick={() => { createQuoteFrom(selected); setSelected(null); }}
                data-testid="request-detail-create-quote-btn"
              >
                <FileText className="w-4 h-4 mr-2" /> Angebot erstellen
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
